import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Disclaimer } from '../shared/Disclaimer'
import { FeedbackModal } from '../shared/FeedbackModal'
import { ShareButton } from '../shared/ShareButton'
import { MobileCostBar } from '../shared/MobileCostBar'
import { ResourcesSection } from '../shared/ResourcesSection'
import { FormulaDisclosure } from '../shared/FormulaDisclosure'
import { DiscountModifier } from '../shared/DiscountModifier'
import { Select } from '../ui/Select'
import { NumberInput } from '../ui/NumberInput'
import { Toggle } from '../ui/Toggle'
import { ScenarioPresets } from '../shared/ScenarioPresets'
import { FieldTooltip } from '../shared/FieldTooltip'
import { useFirewallCalc } from '../../hooks/useCalculate'
import { useAnalytics } from '../../hooks/useAnalytics'
import { useCalculatorStore } from '../../store/calculatorStore'
import type { FirewallRequest, FirewallResponse } from '../../api/types'

const SKU_OPTIONS = [
  { value: 'basic', label: 'Basic', description: 'Lowest deployment fee, highest per-GB processing rate' },
  { value: 'standard', label: 'Standard', description: 'Balanced deployment and data processing pricing' },
  { value: 'premium', label: 'Premium', description: 'Highest deployment fee with advanced inspection features' },
]

const FEATURE_ROWS = [
  {
    label: 'Best fit',
    values: {
      Basic: 'Low-volume branch or simple outbound filtering',
      Standard: 'General production filtering and DNAT/SNAT',
      Premium: 'TLS inspection and advanced security controls',
    },
  },
  {
    label: 'Deployment rate',
    values: {
      Basic: '£0.2972/hr',
      Standard: '£0.9405/hr',
      Premium: '£1.3167/hr',
    },
  },
  {
    label: 'Data processing',
    values: {
      Basic: '£0.0489/GB',
      Standard: '£0.0120/GB',
      Premium: '£0.0120/GB',
    },
  },
]

const DEFAULT_INPUTS: FirewallRequest = {
  sku: 'standard',
  hoursPerMonth: 730,
  dataProcessedGBPerDay: 100,
  loggingEnabled: true,
  logAnalyticsRateGBP: 2.167,
}

export default function FirewallCalc() {
  const [inputs, setInputs] = useState<FirewallRequest>(DEFAULT_INPUTS)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const { discount, setDiscount } = useCalculatorStore()
  const { data, isLoading, isError } = useFirewallCalc(inputs)
  const { trackCalculatorUsed } = useAnalytics()
  useEffect(() => {
    if (data?.monthly_estimate) {
      trackCalculatorUsed({ calculator: 'firewall', result_gbp: data.monthly_estimate, tier: inputs.sku })
    }
  }, [data?.monthly_estimate])

  const set = (partial: Partial<FirewallRequest>) => setInputs((prev) => ({ ...prev, ...partial }))
  const comparisonRows = (data?.tier_comparison ?? []) as NonNullable<FirewallResponse['tier_comparison']>
  const comparisonMap = Object.fromEntries(comparisonRows.map((row) => [row.tier, row]))

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8 w-full pb-14 md:pb-0">
      <div className="flex flex-col gap-6">
        <ScenarioPresets presets={[
          {
            label: 'Hub spoke (Standard)',
            description: 'Standard · always-on · ~1,000 GB/mo — most common enterprise pattern',
            apply: () => set({ sku: 'standard', hoursPerMonth: 730, dataProcessedGBPerDay: 33 }),
          },
          {
            label: 'Internet egress (Premium)',
            description: 'Premium · always-on · ~5,000 GB/mo — shows why logging cost exceeds firewall',
            apply: () => set({ sku: 'premium', hoursPerMonth: 730, dataProcessedGBPerDay: 167 }),
          },
          {
            label: 'Dev environment',
            description: 'Basic · business hours · ~100 GB/mo — shows always-on billing trap',
            apply: () => set({ sku: 'basic', hoursPerMonth: 200, dataProcessedGBPerDay: 3 }),
          },
        ]} />
        <div className={`rounded-lg p-6 ${isLoading ? 'opacity-70' : ''}`} style={{ backgroundColor: 'var(--surface-container-high)', border: '1px solid var(--outline-variant)' }}>
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-6" style={{ color: 'var(--text-primary)' }}>
            <span className="material-symbols-outlined text-[#0078d4]" style={{ fontVariationSettings: "'FILL' 1" }}>shield</span>
            Azure Firewall Configuration
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex flex-col gap-1">
              <div className="flex items-center">
                <span className="text-[11px] font-mono text-outline uppercase tracking-wider">SKU</span>
                <FieldTooltip content="Basic: DNAT only, no threat intel. Standard: full IDPS, URL filtering. Premium adds TLS inspection and IDPS signatures — billing includes a higher deployment rate and data processing fee." />
              </div>
              <Select
                value={inputs.sku}
                onChange={(value) => set({ sku: value as FirewallRequest['sku'] })}
                options={SKU_OPTIONS}
                label=""
              />
            </div>
            <NumberInput
              label="Deployment hours / month"
              value={inputs.hoursPerMonth}
              onChange={(value) => set({ hoursPerMonth: value })}
              min={0}
              max={744}
              step={1}
              inputMode="numeric"
              suffix="hrs"
            />
          </div>

          <div className="mb-6">
            <NumberInput
              label="Data processed per day"
              value={inputs.dataProcessedGBPerDay}
              onChange={(value) => set({ dataProcessedGBPerDay: value })}
              min={0}
              step={1}
              inputMode="decimal"
              suffix="GB/day"
            />
          </div>

          <div className="rounded-sm p-4 mb-6" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)' }}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Estimate diagnostic logging</p>
                <p className="text-[12px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
                  Azure Firewall logs often matter more than the firewall charge itself once the estate grows.
                </p>
              </div>
              <Toggle
                checked={inputs.loggingEnabled}
                onChange={(checked) => set({ loggingEnabled: checked })}
                label="Include logs"
              />
            </div>

            {inputs.loggingEnabled && (
              <div className="mt-4">
                <NumberInput
                  label="Log Analytics rate"
                  value={inputs.logAnalyticsRateGBP}
                  onChange={(value) => set({ logAnalyticsRateGBP: value })}
                  min={0}
                  step={0.001}
                  prefix="£"
                  suffix="/GB"
                  showStepper={false}
                />
              </div>
            )}
          </div>

          <div className="bg-[#0078d4]/10 border border-[#0078d4]/30 p-4 rounded-sm flex gap-3 items-start">
            <span className="material-symbols-outlined text-[#0078d4] text-[18px] shrink-0">info</span>
            <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-primary)' }}>
              At 100 GB/day processed, the model estimates about 0.25 GB/day of Azure Firewall diagnostic logs, or roughly 7.5 GB/month in Log Analytics.
            </p>
          </div>
        </div>

        <div className="rounded-lg p-6" style={{ backgroundColor: 'var(--surface-container-high)', border: '1px solid var(--outline-variant)' }}>
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-4" style={{ color: 'var(--text-primary)' }}>
            <span className="material-symbols-outlined text-[#0078d4]" style={{ fontVariationSettings: "'FILL' 1" }}>table_chart</span>
            SKU comparison
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr style={{ backgroundColor: 'var(--surface-container-low)' }}>
                  <th className="text-left p-3" style={{ color: 'var(--text-tertiary)', borderBottom: '1px solid var(--outline-variant)' }}>Dimension</th>
                  {['Basic', 'Standard', 'Premium'].map((tier) => (
                    <th key={tier} className="text-left p-3" style={{ color: comparisonMap[tier]?.isCurrentSelection ? '#0078d4' : 'var(--text-secondary)', borderBottom: '1px solid var(--outline-variant)' }}>
                      {tier}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURE_ROWS.map((row) => (
                  <tr key={row.label}>
                    <td className="p-3 align-top" style={{ color: 'var(--text-secondary)', borderBottom: '1px solid var(--outline-variant)' }}>{row.label}</td>
                    {['Basic', 'Standard', 'Premium'].map((tier) => (
                      <td key={tier} className="p-3 align-top" style={{ color: 'var(--text-tertiary)', borderBottom: '1px solid var(--outline-variant)' }}>
                        {row.values[tier as keyof typeof row.values]}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="p-3 align-top font-semibold" style={{ color: 'var(--text-secondary)' }}>Monthly firewall cost at current inputs</td>
                  {['Basic', 'Standard', 'Premium'].map((tier) => (
                    <td key={tier} className="p-3 align-top" style={{ color: comparisonMap[tier]?.isCheapest ? '#107c10' : 'var(--text-primary)' }}>
                      {comparisonMap[tier] ? `£${comparisonMap[tier].total.toFixed(2)}` : '—'}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <div className="sticky top-[72px] flex flex-col gap-6">
          <div id="firewall-results" className="rounded-lg overflow-hidden" style={{ backgroundColor: 'var(--surface-container-high)', borderLeft: '2px solid #0078d4' }}>
            <div className="p-6">
              <h3 className="font-mono text-[11px] uppercase tracking-widest mb-2" style={{ color: 'var(--text-tertiary)' }}>Azure Firewall monthly cost (PAYG)</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold font-headline text-[#0078d4]">
                  {isLoading ? '...' : isError ? 'Error' : `£${data?.monthly_estimate.toFixed(2)}`}
                </span>
                <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>/mo</span>
                <button
                  type="button"
                  onClick={() => setFeedbackOpen(true)}
                  className="ml-1 transition-colors"
                  style={{ color: 'var(--text-tertiary)' }}
                  title="Report an issue with this estimate"
                >
                  <span className="material-symbols-outlined text-[18px]">help</span>
                </button>
              </div>
              {data && (
                <p className="text-[12px] mt-3" style={{ color: 'var(--text-tertiary)' }}>
                  With estimated logs: <span style={{ color: 'var(--text-primary)' }}>£{data.totalWithLogging.toFixed(2)}/mo</span>
                </p>
              )}
            </div>

            <div className="px-6 py-4" style={{ backgroundColor: 'var(--surface-container-low)' }}>
              <h4 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--text-tertiary)' }}>Firewall breakdown</h4>
              <ul className="space-y-3">
                {data?.breakdown.map((item, idx) => (
                  <li key={idx} className="flex items-center justify-between gap-3 text-[12px] font-mono">
                    <span style={{ color: 'var(--text-tertiary)' }}>{item.label}</span>
                    <span style={{ color: 'var(--text-primary)' }}>£{item.value.toFixed(2)}</span>
                  </li>
                ))}
                {!data && !isLoading && (
                  <li className="text-[12px] text-center py-2" style={{ color: 'var(--text-tertiary)' }}>
                    Adjust inputs to see pricing.
                  </li>
                )}
              </ul>
            </div>

            {data && (
              <div className={`p-4 ${data.estimated_logging.enabled ? 'bg-[#e8b000]/10 border-t border-[#e8b000]/30' : 'border-t'}`} style={data.estimated_logging.enabled ? {} : { borderColor: 'var(--outline-variant)', backgroundColor: 'var(--surface-container)' }}>
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-[18px] shrink-0" style={{ color: data.estimated_logging.enabled ? '#e8b000' : 'var(--text-tertiary)' }}>receipt_long</span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                      Estimated logging cost: £{data.estimated_logging.cost.toFixed(2)}/month
                    </p>
                    <p className="text-[12px] mt-1" style={{ color: 'var(--text-secondary)' }}>
                      Based on {data.estimated_logging.gb_per_month.toFixed(2)} GB/month of Firewall logs. Your logging cost is {data.estimated_logging.percent_of_firewall_cost.toFixed(1)}% of your Firewall cost.
                    </p>
                    <Link to={data.logAnalyticsCrossLinkURL} className="inline-flex items-center gap-1 text-[12px] mt-3 text-[#0078d4] hover:underline">
                      Open in Log Analytics calculator
                      <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {data && data.warnings && data.warnings.length > 0 && (
              <div className="border-t border-[#e8b000]/30 bg-[#e8b000]/5 p-4 space-y-2">
                {data.warnings.map((warning, index) => (
                  <p key={index} className="text-[11px] flex items-start gap-2" style={{ color: '#e8b000' }}>
                    <span className="material-symbols-outlined text-[14px] shrink-0 mt-0.5">warning</span>
                    {warning}
                  </p>
                ))}
              </div>
            )}

            {data && (
              <div className="px-6 pb-2">
                <FormulaDisclosure
                  lines={[
                    ...data.breakdown.map((item) => `${item.label}: £${item.value.toFixed(4)}`),
                    `Estimated logging: ${data.estimated_logging.enabled ? `£${data.estimated_logging.cost.toFixed(4)} (${data.estimated_logging.gb_per_month.toFixed(4)} GB/month × £${data.estimated_logging.rate_gbp_per_gb.toFixed(4)}/GB)` : 'Disabled'}`,
                    `SKU: ${inputs.sku} · Hours: ${inputs.hoursPerMonth} · Data: ${inputs.dataProcessedGBPerDay} GB/day · Region: UK South`,
                  ]}
                  fetchedAt={data.prices_last_updated}
                />
              </div>
            )}
          </div>

          {data && (
            <DiscountModifier
              discount={discount}
              onDiscountChange={setDiscount}
              paygTotal={data.monthly_estimate}
            />
          )}

          <div className="p-4 text-[10px] leading-relaxed" style={{ backgroundColor: 'var(--surface-container-lowest)', border: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}>
            <Disclaimer />
          </div>

          <ShareButton inputs={inputs} result={data ?? null} service="firewall" />
          <ResourcesSection service="firewall" />
        </div>
      </div>

      {feedbackOpen && (
        <FeedbackModal
          service="firewall"
          estimateValue={data ? `£${data.monthly_estimate.toFixed(2)}/mo` : 'N/A'}
          onClose={() => setFeedbackOpen(false)}
        />
      )}

      <MobileCostBar amount={data?.monthly_estimate} label="Azure Firewall" targetId="firewall-results" />
    </div>
  )
}
